import { format, getISOWeeksInYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getWeekDates } from './getWeekDates.js';
import { addParisDays, overlapDays, parisParts, periodDays, reportPeriodOf, sameParisDay } from './reportPeriod.js';

/** ISO years have 52 or 53 weeks (2026 has 53). */
export const weeksInYear = (year) => getISOWeeksInYear(new Date(parseInt(year, 10), 0, 4));

/** "14 → 20 sept. 2026", "28 sept. → 4 oct. 2026", "29 déc. 2025 → 4 janv. 2026". */
export const formatWeekRange = (year, weekNumber) => {
  const { startDate, endDate } = getWeekDates(year, weekNumber);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();
  const start = format(startDate, sameMonth ? 'd' : sameYear ? 'd MMM' : 'd MMM yyyy', { locale: fr });
  return `${start} → ${format(endDate, 'd MMM yyyy', { locale: fr })}`;
};

/** "Du lundi 14 au dimanche 20 septembre 2026". */
export const formatWeekRangeLong = (year, weekNumber) => {
  const { startDate, endDate } = getWeekDates(year, weekNumber);
  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
  const start = format(startDate, sameMonth ? 'EEEE d' : 'EEEE d MMMM', { locale: fr });
  return `Du ${start} au ${format(endDate, 'EEEE d MMMM yyyy', { locale: fr })}`;
};

/** Newest first: year then week, compared as numbers (both are stored as strings). */
export const compareWeeksDesc = (a, b) => (
  (parseInt(b.year, 10) - parseInt(a.year, 10)) || (parseInt(b.week_number, 10) - parseInt(a.week_number, 10))
);

const DAY_NAMES = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const DAY_ABBR = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const MONTH_ABBR = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];

export const dayName = (day) => DAY_NAMES[(+day || 1) - 1] || DAY_NAMES[0];

export const formatSlot = (day, hour) => `${dayName(day)} à ${+hour || 0} h`;

export const formatDays = (days) => String(Math.round(+days * 10) / 10).replace('.', ',');

/** "jeu 22 oct 13 h" in Paris time; `separator: ' à '` gives "jeu 22 oct à 13 h". Year only when not the current one. */
export const formatParisInstant = (instant, { separator = ' ', now = new Date() } = {}) => {
  const p = parisParts(instant);
  const year = p.year !== parisParts(now).year ? ` ${p.year}` : '';
  return `${DAY_ABBR[p.weekday - 1]} ${p.day} ${MONTH_ABBR[p.month - 1]}${year}${separator}${p.hour} h`;
};

/** "aujourd'hui à 13 h" for today in Paris, else "jeu 22 oct à 13 h". */
export const formatParisWhen = (instant, now = new Date()) => (sameParisDay(instant, now)
  ? `aujourd'hui à ${parisParts(instant).hour} h`
  : formatParisInstant(instant, { separator: ' à ', now }));

const time = (value) => (value ? new Date(value).getTime() : NaN);

/** True for a Monday 0 h → Monday 0 h period, and for legacy rows without a period. */
export const isStandardWeek = (report) => {
  const start = time(report?.period_start);
  const end = time(report?.period_end);
  if (Number.isNaN(start) || Number.isNaN(end)) return true;
  const p = parisParts(start);
  return p.weekday === 1 && p.hour === 0 && p.minute === 0 && addParisDays(start, 7).getTime() === end;
};

export const formatParisRange = ({ start, end }, options) => `${formatParisInstant(start, options)} → ${formatParisInstant(end, options)}`;

export const formatPeriodRange = (report, options) => formatParisRange({ start: report.period_start, end: report.period_end }, options);

export const formatReportRange = (report, { long = false } = {}) => {
  if (isStandardWeek(report)) return (long ? formatWeekRangeLong : formatWeekRange)(report?.year, report?.week_number);
  return formatPeriodRange(report);
};

/** Title + subtitle of a report row: unchanged for Monday 0 h weeks, the real period otherwise. */
export const reportRowLabels = (report, overlap) => {
  if (isStandardWeek(report)) return { title: `Semaine ${report.week_number}`, subtitle: formatWeekRange(report.year, report.week_number) };
  const slot = dayName(parisParts(report.period_end).weekday);
  const days = periodDays(report.period_start, report.period_end);
  return {
    title: formatPeriodRange(report),
    subtitle: overlap || days !== 7 ? `Transition vers le créneau du ${slot}` : `Créneau du ${slot} · ${formatDays(days)} jours`,
  };
};

/** id → { days, week } for each report of a newest-first list that re-covers part of the previous one. */
export const overlapsByReport = (reports = []) => {
  const map = new Map();
  reports.forEach((report, i) => {
    const previous = reports[i + 1];
    const days = overlapDays(previous?.period_end, report.period_start, report.period_end);
    if (days > 0) map.set(report.id, { days, week: previous.week_number });
  });
  return map;
};

export { isSnapshot } from './reportPeriod.js';

const sortKey = (report) => time(reportPeriodOf(report).start);

/** Newest first by period start; rows without a period use their ISO week's Monday. */
export const compareReportsDesc = (a, b) => (sortKey(b) - sortKey(a)) || compareWeeksDesc(a, b);

/** What « Générer maintenant » does with a generateNowPeriod() result. */
export const describeGenerateNow = (period, now = new Date()) => {
  const end = formatParisInstant(period.end, { separator: ' à ', now });
  return period.ongoing
    ? `met à jour l'instantané S${period.week} en cours, complété le ${end}`
    : `crée un instantané ${formatParisInstant(period.start, { now })} → maintenant, complété le ${end}`;
};

/** « Reprend 3,5 jours déjà présents dans S42. » when the period starts before `previous` ends. */
export const overlapNote = (previous, period) => {
  const days = previous ? overlapDays(reportPeriodOf(previous).end, period.start, period.end) : 0;
  return days > 0 ? `Reprend ${formatDays(days)} jours déjà présents dans S${previous.week_number}.` : null;
};
